import type { ComponentProps } from 'react';
import { Button, Tooltip } from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAuth } from '@/services/auth';
import { PlanFeature } from '@/services/auth/types';
import { GatedFeaturesPopover } from '@/features/gated-features';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { useAnalytics } from '@/services/analytics';
import { useCollectionFormModal } from './collection-form-modal';

type AddCollectionButtonProps = {
  workspaceId: number;
  existingNames: string[];
  maxCollectionCount: number | undefined;
  maxFileSizeBytes: number | undefined;
  variant?: ComponentProps<typeof Button>['variant'];
};

export function AddCollectionButton({
  workspaceId,
  existingNames,
  maxCollectionCount,
  maxFileSizeBytes,
  variant = 'primary',
}: AddCollectionButtonProps) {
  const { account } = useAuth();
  const { hasPermission } = usePermissions();
  const taxonomy = useProductTaxonomy();
  const collectionModal = useCollectionFormModal();
  const analytics = useAnalytics();

  const isEssentialsTrial = account.suspend_trial_at != null;

  if (!account.planFeatures[PlanFeature.Collections].enabled) {
    return (
      <GatedFeaturesPopover
        featureName="Collections"
        title={`Unlock Collections with Premium ${isEssentialsTrial ? 'Trial' : ''}`.trim()}
        text={`With a Premium ${isEssentialsTrial ? 'Trial' : 'Plan'} you can store collections of data in your workspace that you can use in ${taxonomy.term('campaigns.plural')} with Liquid.`}
        variant={variant}
      >
        Add collection
      </GatedFeaturesPopover>
    );
  }

  const canCreate = hasPermission('journeys/collections/create') === true;
  // Only block once the limit is known — blocking on an assumed default would disable
  // creation for a workspace whose real limit is higher while the settings load.
  const reachedLimit = maxCollectionCount != null && existingNames.length >= maxCollectionCount;

  if (reachedLimit) {
    return (
      <Tooltip
        isCustom
        content={`Workspaces are limited to ${maxCollectionCount} collections. Remove one to continue.`}
      >
        {(context) => (
          <Button
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            variant={variant}
            isDisabled
            onClick={() => collectionModal.open({ workspaceId, existingNames, maxFileSizeBytes })}
          >
            Add collection
          </Button>
        )}
      </Tooltip>
    );
  }

  return (
    <Button
      variant={variant}
      isDisabled={!canCreate}
      onClick={() => {
        void analytics.track('collections', 'creationStarted', {});
        collectionModal.open({ workspaceId, existingNames, maxFileSizeBytes });
      }}
    >
      Add collection
    </Button>
  );
}
